import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../api/axios";
import "./SboePage.css";

import CriarModal from "./popups/CriarModal.jsx";
import EditarModal from "./popups/EditarModal.jsx";
import ExcluirModal from "./popups/ExcluirModal.jsx";

const UP = (v) => (typeof v === "string" ? v.trim().toUpperCase() : "");
const norm = (v) => (v == null ? "" : String(v));
const NUM = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : null;
};

function IconEdit(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

async function apiList(params) {
  try {
    return await api.get("/bases/sb-origem-encerramento/listar", { params });
  } catch {
    return await api.get("/sb-origem-encerramento/listar", { params });
  }
}

async function apiCreate(body) {
  try {
    return await api.post("/bases/sb-origem-encerramento", body);
  } catch {
    return await api.post("/sb-origem-encerramento", body);
  }
}

async function apiUpdate(id, body) {
  try {
    return await api.put(`/bases/sb-origem-encerramento/${id}`, body);
  } catch {
    return await api.put(`/sb-origem-encerramento/${id}`, body);
  }
}

async function apiRemove(id) {
  try {
    return await api.delete(`/bases/sb-origem-encerramento/${id}`);
  } catch {
    return await api.delete(`/sb-origem-encerramento/${id}`);
  }
}

export default function SboePage({ onBack }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [subFiltro, setSubFiltro] = useState(null);

  const [openCriar, setOpenCriar] = useState(false);
  const [editarRow, setEditarRow] = useState(null);
  const [excluirRow, setExcluirRow] = useState(null);

  useEffect(() => {
    document.body.classList.add("sboe-skin");
    return () => document.body.classList.remove("sboe-skin");
  }, []);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await apiList({ limit: 2000, offset: 0 });
      const list = Array.isArray(data?.data) ? data.data : [];
      const sorted = [...list].sort((a, b) => {
        const na = NUM(a?.sub);
        const nb = NUM(b?.sub);
        if (na != null && nb != null && na !== nb) return na - nb;
        if (na != null && nb == null) return -1;
        if (na == null && nb != null) return 1;

        const sa = norm(a?.sub);
        const sb = norm(b?.sub);
        const bySub = sa.localeCompare(sb, "pt-BR", { sensitivity: "base", numeric: true });
        if (bySub !== 0) return bySub;

        const sba = norm(a?.sb);
        const sbb = norm(b?.sb);
        const bySb = sba.localeCompare(sbb, "pt-BR", { sensitivity: "base" });
        if (bySb !== 0) return bySb;

        return Number(a?.id || 0) - Number(b?.id || 0);
      });
      setRows(sorted);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || "Erro ao listar");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const stats = useMemo(() => {
    const total = rows.length;
    const subs = new Set();
    const sbs = new Set();
    const porSub = {};

    for (const row of rows) {
      const sub = UP(row?.sub) || "-";
      const sb = UP(row?.sb) || "-";
      subs.add(sub);
      sbs.add(sb);
      porSub[sub] = (porSub[sub] || 0) + 1;
    }

    const chips = Object.entries(porSub).sort((a, b) =>
      String(a[0]).localeCompare(String(b[0]), "pt-BR", { sensitivity: "base", numeric: true })
    );

    return { total, subs: subs.size, sbs: sbs.size, chips };
  }, [rows]);

  const visiveis = useMemo(() => {
    const qq = UP(q);
    let list = subFiltro ? rows.filter((r) => UP(r?.sub) === UP(subFiltro)) : rows;

    if (qq) {
      list = list.filter((r) => {
        const sub = UP(r?.sub);
        const sb = UP(r?.sb);
        return sub.includes(qq) || sb.includes(qq);
      });
    }

    return list;
  }, [rows, q, subFiltro]);

  function goHome() {
    if (onBack) onBack();
    else if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  async function handleCriar(payload) {
    try {
      await apiCreate({ sub: UP(payload?.sub), sb: UP(payload?.sb) });
      setOpenCriar(false);
      await carregar();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao cadastrar");
    }
  }

  async function handleEditar(payload) {
    try {
      if (!editarRow?.id) return;
      await apiUpdate(editarRow.id, { sub: UP(payload?.sub), sb: UP(payload?.sb) });
      setEditarRow(null);
      await carregar();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao editar");
    }
  }

  async function handleExcluir() {
    try {
      if (!excluirRow?.id) return;
      await apiRemove(excluirRow.id);
      setExcluirRow(null);
      await carregar();
    } catch (e) {
      alert(e?.response?.data?.error || "Erro ao excluir");
    }
  }

  function renderColGroup() {
    return (
      <colgroup>
        <col style={{ width: "42%" }} />
        <col style={{ width: "42%" }} />
        <col style={{ width: "16%" }} />
      </colgroup>
    );
  }

  return (
    <div className="sboe-shell">
      <div className="sboe-topbar">
        <button className="sboe-btn-hero left" type="button" onClick={goHome}>
          Voltar para o início
        </button>

        <h1 className="sboe-title">Seção de Bloqueio de Origem e Encerramento</h1>

        <button className="sboe-btn-hero right" type="button" onClick={() => setOpenCriar(true)}>
          Cadastrar
        </button>
      </div>

      <div className="sboe-head">
        <div className="sboe-cards-row sboe-cards--all">
          <div className="sboe-search-panel">
            <div className="sboe-search-panel__label">PESQUISA</div>
            <input
              className="sboe-search"
              placeholder="Buscar por SUB ou SB..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="sboe-card">
            <div className="sboe-card__label">TOTAL</div>
            <div className="sboe-card__value">{stats.total}</div>
          </div>

          <div className="sboe-card sboe-card--ok">
            <div className="sboe-card__label">SUBS</div>
            <div className="sboe-card__value">{stats.subs}</div>
          </div>

          <div className="sboe-card sboe-card--ok">
            <div className="sboe-card__label">SBS</div>
            <div className="sboe-card__value">{stats.sbs}</div>
          </div>

          <div className="sboe-chips-panel">
            <div className="sboe-chips-panel__label">POR SUB</div>
            <div className="sboe-chip-list">
              {stats.chips.map(([sub, qtd]) => (
                <button
                  key={sub}
                  className={`sboe-chip ${subFiltro && UP(subFiltro) === UP(sub) ? "active" : ""}`}
                  type="button"
                  onClick={() => setSubFiltro((prev) => (prev && UP(prev) === UP(sub) ? null : sub))}
                  title={`Filtrar por ${sub}`}
                >
                  {sub}
                  <span className="sboe-chip__num">{qtd}</span>
                </button>
              ))}

              {subFiltro && (
                <button className="sboe-chip sboe-chip--clear" type="button" onClick={() => setSubFiltro(null)}>
                  Limpar filtro x
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="sboe-wrap">
        {loading ? (
          <div className="sboe-empty">Carregando...</div>
        ) : err ? (
          <div className="sboe-empty">{err}</div>
        ) : visiveis.length === 0 ? (
          <div className="sboe-empty">Nenhum registro encontrado.</div>
        ) : (
          <div className="sboe-table-wrap">
            <table className="sboe-table sboe-table--head">
              {renderColGroup()}
              <thead>
                <tr>
                  <th>SUB</th>
                  <th>SB</th>
                  <th>CONTROLES</th>
                </tr>
              </thead>
            </table>

            <div className="sboe-table-body-scroll">
              <table className="sboe-table sboe-table--body">
                {renderColGroup()}
                <tbody>
                  {visiveis.map((row) => (
                    <tr key={row.id}>
                      <td className="sboe-td sboe-td-strong">{UP(row.sub) || "-"}</td>
                      <td className="sboe-td sboe-td-strong">{UP(row.sb) || "-"}</td>
                      <td className="sboe-td sboe-td-controls">
                        <div className="sboe-actions sboe-actions--cluster">
                          <button className="sboe-icon-btn" title="Editar" onClick={() => setEditarRow(row)}>
                            <IconEdit />
                          </button>
                          <span className="sboe-actions-divider" aria-hidden="true" />
                          <button className="sboe-icon-btn" title="Excluir" onClick={() => setExcluirRow(row)}>
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {openCriar && (
        <CriarModal open onClose={() => setOpenCriar(false)} onSalvar={handleCriar} />
      )}

      {editarRow && (
        <EditarModal
          open
          registro={editarRow}
          onClose={() => setEditarRow(null)}
          onSalvar={handleEditar}
        />
      )}

      {excluirRow && (
        <ExcluirModal
          open
          registro={excluirRow}
          onClose={() => setExcluirRow(null)}
          onConfirm={handleExcluir}
        />
      )}
    </div>
  );
}
